import { Options } from './@types/commands';
import { Environment } from './@types/environment';
import { PageOptions } from './@types/page-options';
import { logError } from './logger';
import {
  getActiveWindow,
  getLastFriendlyFrameElement,
  getTopWindow,
  hasCreateElementOverride,
  hasWindowTopEventListener,
  isAmpPage,
} from './utils/dom';
import { getSafeFrame, isInIframe } from './utils/iframe';
import {
  canTrackUser,
  getPageDomain,
  getPageUrl,
  isAppnexusAPN,
  isEnsighten,
  isMraid,
} from './utils/page';

export const getEnvironment = (() => {
  let environment: Environment;
  return (globalScope: Window = window) => {
    // environment already created, return cached value
    if (environment) {
      return environment;
    }
    try {
      const frameElement = getLastFriendlyFrameElement();
      const hasEventListener = hasWindowTopEventListener(globalScope);
      const topWindow = getTopWindow(globalScope, frameElement);
      const { safeFrame, isRegistered } = getSafeFrame();

      environment = {
        isIframe: isInIframe(),
        isAmp: isAmpPage(),
        currentWindow: globalScope,
        topWindow,
        frameElement,
        isUnfriendly: !frameElement && !hasEventListener,
        safeFrame,
        isSafeFrameRegistered: isRegistered,
        activeWindow: getActiveWindow(),
        url: getPageUrl(),
        domain: getPageDomain(),
        mraid: isMraid(),
        isEnsighten: isEnsighten(topWindow),
        isAppnexusAPN: isAppnexusAPN(topWindow),
        createElementOverridden: hasCreateElementOverride(topWindow),
        doNotTrack: canTrackUser(globalScope),

        // jest.resetModules() doesn't work with this module because it's cached
        // so provide a way to reset the environment for testing
        resetEnvironment: () => {
          if (typeof jest === 'undefined') {
            throw new Error('RESET ENVIRONMENT SHOULD ONLY BE USED IN TESTS!');
          }
          environment = undefined as any;
        },
      };
    } catch (e) {
      logError({
        reason: 'lib-err',
        message: 'environment setup error',
        tagId: '0',
        impressionId: '',
        detail: (e as Error).stack || e,
        error: (e as Error).stack || e,
      });
    }

    return environment;
  };
})();

/**
 * Debug levels are set from the server
 * debug verbose is set via a header/query from the
 * debug extension plugin on tagservice urls. This is controlled
 * from the aurelius repo/package extension manifest.json
 */
export const DEBUG_OFF = 0;
export const DEBUG_ERROR = 1;
export const DEBUG_INFO = 2;
export const DEBUG_VERBOSE = 3;

// From webpack build - overridden with current package version
declare var __VERSION__: string;

/**
 * Options are mutated over the course of the applications lifecycle
 * usually from server events
 */
let options: Partial<PageOptions> &
  Pick<PageOptions, 'endpoint' | 'debug' | 'version'> = {
  endpoint: 'tagservice.maximus.mobkoi.com',
  debug: DEBUG_OFF,
  version: __VERSION__,
};

export const getOptions = () => ({ ...options });

export const addOptions = (newOptions: Options | Partial<PageOptions>) => {
  options = { ...options, ...newOptions };
};
